import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraInput, SpecraSelect, SpecraSwitch, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageBody, PageHero } from '../../components/layout';
import { useRecordBreadcrumbs } from '../../components/RecordShell/useRecordBreadcrumbs';
import { useAuth } from '../../contexts/authContext';
import { tenantRootRef } from '../../lib/access/useTenantAreas';
import { useView } from '../../lib/access/useView';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { growthCrumbs } from '../GrowthHub/growthCrumbs';
import { useVehicleDossier } from '../Registries/records/vehicle/hooks';
import { AccessNote } from './components/AccessNote';
import { CriterionValueField } from './components/CriterionValueField';
import { LandscapeOrganizationsField } from './components/LandscapeOrganizationsField';
import { useListSeats, useSetLandscapeSeat } from './hooks/useLandscapeSeats';
import { useLandscape, useSaveLandscape } from './hooks/useLandscapes';
import { CRITERION_KINDS, EMPTY_FORM, fromLandscape, LandscapeCriterionKind, toLandscapeInput } from './landscapeForm';
const Field = ({ label, children })=>/*#__PURE__*/ _jsx(SpecraFormField, {
        label: label,
        children: children
    });
/** Create (`/landscapes/new`) and edit (`/landscapes/:id/edit`); saving never runs the rules. */ export function LandscapeFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const writeAccess = useView(tenantRootRef(user?.tenant_id ?? '', 'opportunity'), {
        action: id ? 'update' : 'create',
        enabled: !!user?.tenant_id
    });
    const canWrite = writeAccess.can(id ? 'update' : 'create');
    const existing = useLandscape(id ?? '');
    const save = useSaveLandscape(id);
    const [form, setForm] = useState(EMPTY_FORM);
    // "Open as a landscape" from a vehicle record: create mode only. Anything that fails to load
    // leaves the blank form exactly as before.
    const [params] = useSearchParams();
    const fromVehicle = !id && params.get('fromVehicle') || '';
    const [attaching, setAttaching] = useState(false);
    const vehicleName = useVehicleDossier(fromVehicle).data?.name;
    const vehicleSeats = useListSeats({
        vehicleId: fromVehicle
    }, !!fromVehicle);
    const seatsToAttach = vehicleSeats.data?.growthHidden ? [] : vehicleSeats.data?.seats ?? [];
    const setSeat = useSetLandscapeSeat();
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        setShowHeader
    ]);
    useRecordBreadcrumbs(growthCrumbs({
        label: 'Landscapes',
        href: '/landscapes'
    }, ...id ? [
        {
            label: existing.data?.name ?? 'Landscape',
            href: `/landscapes/${id}`
        }
    ] : [], {
        label: id ? 'Edit Landscape' : 'New Landscape'
    }));
    useEffect(()=>{
        if (existing.data) setForm(fromLandscape(existing.data));
    }, [
        existing.data
    ]);
    useEffect(()=>{
        if (vehicleName) setForm((f)=>f.name ? f : {
                ...f,
                name: `${vehicleName} recompetes`
            });
    }, [
        vehicleName
    ]);
    const patch = (p)=>setForm((f)=>({
                ...f,
                ...p
            }));
    const setCriterion = (i, p)=>patch({
            criteria: form.criteria.map((c, j)=>j === i ? {
                    ...c,
                    ...p
                } : c)
        });
    const submit = ()=>{
        const input = toLandscapeInput(form);
        if (!input.name) return;
        save.mutate(input, {
            onSuccess: async (landscape)=>{
                // The landscape exists either way, so a failed seat attach never strands the user on the
                // form (a retry would create a duplicate landscape): go there and say what is missing.
                if (landscape && seatsToAttach.length > 0) {
                    setAttaching(true);
                    const results = await Promise.allSettled(seatsToAttach.map((seat, sortOrder)=>setSeat.mutateAsync({
                            landscapeId: landscape.id,
                            seatId: seat.id,
                            member: true,
                            sortOrder
                        })));
                    setAttaching(false);
                    const failed = results.filter((r)=>r.status === 'rejected').length;
                    if (failed > 0) {
                        toast.error(`Saved Landscape "${input.name}", but ${failed} of ${seatsToAttach.length} seats could not be attached. Add them from the Landscape's seats.`);
                        navigate(`/landscapes/${landscape.id}`);
                        return;
                    }
                }
                toast.success(`Saved Landscape "${input.name}"`);
                navigate(landscape ? `/landscapes/${landscape.id}` : '/landscapes');
            },
            onError: ()=>toast.error('Could not save Landscape')
        });
    };
    return /*#__PURE__*/ _jsx("div", {
        children: /*#__PURE__*/ _jsxs(PageBody, {
            padding: "page",
            children: [
                /*#__PURE__*/ _jsx(PageHero, {
                    title: id ? 'Edit Landscape' : 'New Landscape',
                    subtitle: "Describe the slice of the market; criteria suggest members when you run the rules"
                }),
                /*#__PURE__*/ _jsxs("form", {
                    className: "max-w-2xl space-y-4",
                    "data-testid": Testids.landscapes.form,
                    onSubmit: (e)=>{
                        e.preventDefault();
                        submit();
                    },
                    children: [
                        /*#__PURE__*/ _jsx(AccessNote, {}),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Name",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                disabled: !canWrite,
                                value: form.name,
                                onChange: (e)=>patch({
                                        name: e.target.value
                                    }),
                                "data-testid": Testids.landscapes.nameInput
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Scope",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: form.scope,
                                onChange: (e)=>patch({
                                        scope: e.target.value
                                    }),
                                placeholder: "What this Landscape covers, in words",
                                "data-testid": Testids.landscapes.scopeInput
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Organizations (optional)",
                            children: /*#__PURE__*/ _jsx(LandscapeOrganizationsField, {
                                value: form.organizations,
                                onChange: (organizations)=>patch({
                                        organizations
                                    }),
                                disabled: !canWrite
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Capabilities (optional, comma separated)",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: form.capabilities,
                                onChange: (e)=>patch({
                                        capabilities: e.target.value
                                    }),
                                "data-testid": Testids.landscapes.capabilitiesInput
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Context (optional)",
                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                value: form.context,
                                onChange: (e)=>patch({
                                        context: e.target.value
                                    }),
                                "data-testid": Testids.landscapes.contextInput
                            })
                        }),
                        seatsToAttach.length > 0 && /*#__PURE__*/ _jsxs("fieldset", {
                            className: "space-y-1",
                            "data-testid": Testids.landscapes.seatsToAttach,
                            children: [
                                /*#__PURE__*/ _jsxs("legend", {
                                    className: "text-sm font-medium",
                                    children: [
                                        "Seats to attach (",
                                        seatsToAttach.length,
                                        ")"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    className: "text-sm text-muted-foreground",
                                    children: seatsToAttach.map((seat)=>/*#__PURE__*/ _jsxs("li", {
                                            "data-testid": Testids.landscapes.seatToAttachRow,
                                            children: [
                                                seat.name,
                                                seat.customerLabel ? ` · ${seat.customerLabel}` : ''
                                            ]
                                        }, seat.id))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2 text-sm",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraSwitch, {
                                    "aria-label": "Observed only",
                                    checked: form.observedOnly,
                                    onCheckedChange: (observedOnly)=>patch({
                                            observedOnly
                                        }),
                                    "data-testid": Testids.landscapes.observedOnlyToggle
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    children: "Observed only"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("fieldset", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx("legend", {
                                    className: "text-sm font-medium",
                                    children: "Criteria"
                                }),
                                form.criteria.map((c, i)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2",
                                        "data-testid": Testids.landscapes.criterionRow,
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraSelect, {
                                                size: "sm",
                                                value: c.kind,
                                                onChange: (e)=>setCriterion(i, {
                                                        kind: Number(e.target.value),
                                                        value: ''
                                                    }),
                                                "aria-label": "Criterion kind",
                                                "data-testid": Testids.landscapes.criterionKind,
                                                children: CRITERION_KINDS.map((k)=>/*#__PURE__*/ _jsx("option", {
                                                        value: k.kind,
                                                        children: k.label
                                                    }, k.kind))
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "min-w-0 flex-1",
                                                children: /*#__PURE__*/ _jsx(CriterionValueField, {
                                                    kind: c.kind,
                                                    value: c.value,
                                                    onChange: (value)=>setCriterion(i, {
                                                            value
                                                        })
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                type: "button",
                                                size: "sm",
                                                variant: "ghost",
                                                "aria-label": "Remove criterion",
                                                onClick: ()=>patch({
                                                        criteria: form.criteria.filter((_, j)=>j !== i)
                                                    }),
                                                "data-testid": Testids.landscapes.removeCriterionButton,
                                                children: /*#__PURE__*/ _jsx(X, {
                                                    className: "h-4 w-4"
                                                })
                                            })
                                        ]
                                    }, i)),
                                /*#__PURE__*/ _jsxs(SpecraButton, {
                                    type: "button",
                                    size: "sm",
                                    variant: "outline",
                                    onClick: ()=>patch({
                                            criteria: [
                                                ...form.criteria,
                                                {
                                                    kind: LandscapeCriterionKind.AGENCY,
                                                    value: ''
                                                }
                                            ]
                                        }),
                                    "data-testid": Testids.landscapes.addCriterionButton,
                                    children: [
                                        /*#__PURE__*/ _jsx(Plus, {
                                            className: "h-4 w-4 mr-1"
                                        }),
                                        "Add criterion"
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            type: "submit",
                            disabled: !canWrite || !form.name.trim() || save.isPending || attaching,
                            "data-testid": Testids.landscapes.submitButton,
                            children: "Save Landscape"
                        })
                    ]
                })
            ]
        })
    });
}
